import { Component, HostBinding, Input, OnInit } from '@angular/core';
import { AnimationDefinitions } from '@meddev/fe-shared';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import * as moment from 'moment';
import { EMPTY, from, iif, Observable, of } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { AppModulesType } from '../../../../../../../_shared/enums/appModulesType.enum';
import { CusomerCreatedFromType } from '../../../../../../../_shared/enums/customer/cusomerCreatedFromType.enum';
import { ReservationExaminationStatus } from '../../../../../../../_shared/enums/examination/reservation-examination-status.enum';
import { DocumentationKanbanStatus } from '../../../../../../../_shared/enums/kanban/documentationKanbanStatus.enum';
import { InvoiceKanbanStatus } from '../../../../../../../_shared/enums/kanban/invoiceKanbanStatus.enum';
import { PaperType } from '../../../../../../../_shared/enums/PaperType.enum';
import { ReservationMode } from '../../../../../../../_shared/enums/reservationMode.enum';
import { PortfolioPaper } from '../../../../../../../_shared/model';
import { MfToastService } from '../../../../../../../_shared/services';
import { ActiveModulesService } from '../../../../../../../_shared/services/activeModules.service';
import { SimilarCustomersService } from '../../../../../../../_shared/services/similar-customers.service';
import { DocumentationStatus } from '../../../../../../components/documentation-status-list/model/documentation-status.model';
import { ReservationExaminationViewItem } from '../../../../terms/visit/selected-term/examinations/model/reservation-examination-view-item';
import { ReservationStatusType } from './../../../../../../../_shared/enums/reservationStatusType.enum';
import { Customer } from './../../../../../../../_shared/model/customer.model';
import { Event } from './../../../../../../../_shared/model/event.model';
import { Reservation } from './../../../../../../../_shared/model/reservation.model';
import { ReservationRequest } from './../../../../../../../_shared/model/reservationRequest.model';
import { Subcontractor } from './../../../../../../../_shared/model/subcontractor.model';
import { HelperNavigateService } from './../../../../../../../_shared/services/helper-navigate.service';
import { TermPopupComponent } from './../../../../../../components/term-popup/term-popup.component';
import { KbItem } from './../../model/kb-item.model';

@Component({
    selector: 'inx-kab-item',
    templateUrl: './inx-kab-item.component.html',
    styleUrls: ['./inx-kab-item.component.scss'],
    animations: [AnimationDefinitions.ExpandCollapseHeight],
})
export class InxKabItemComponent implements OnInit {
    @Input() data: KbItem;
    @Input() showDate = false;
    @Input() draggable = true;

    missingData = false;
    isShowDetails = false;
    isSingleClick = false;
    isExaminationViewVisible = false;

    examinationsForReservationView$: Observable<ReservationExaminationViewItem[]>;
    examinationsProgressView$: Observable<number>;
    statusView$: Observable<DocumentationStatus[]>;

    isMdpsModuleActive = this.am.isAM(AppModulesType.OCCUPATIONAL_MEDICINE);

    reservationExaminationStatus = ReservationExaminationStatus;

    @HostBinding('class.draggable') get draggableClass() {
        return this.draggable;
    }

    constructor(
        private navigate: HelperNavigateService,
        private modalService: NgbModal,
        private am: ActiveModulesService,
        private similarCustomersService: SimilarCustomersService,
        private toast: MfToastService,
    ) {}

    ngOnInit() {
        const customer: Customer = this.data.data.customer;
        if (!customer?.address || !customer?.city) {
            this.missingData = true;
        } else {
            this.missingData = false;
        }

        if (this.isMdpsModuleActive || this.am.isAM(AppModulesType.LABORATORY)) {
            this.isExaminationViewVisible = true;
        }

        this.examinationsForReservationView$ = this.createViewItems$();
        this.examinationsProgressView$ = this.createProgressView$();
        this.statusView$ = this.createStatusView$();
    }

    openReservation(): void {
        of(this.data?.data as unknown as Event)
            .pipe(
                tap(() => (this.isSingleClick = false)),
                switchMap(event => {
                    return iif(
                        () => event.customer?.createdFrom == CusomerCreatedFromType.PATIENT_APP,
                        this.similarCustomersService.findSimilarCustomers(event.contractor.id, event, false).pipe(
                            tap(result => {
                                if (!result) {
                                    throw new Error('');
                                }
                            }),
                        ),
                        of(null),
                    );
                }),
                catchError(() => {
                    this.toast.error('Podatki stranke niso urejeni.');
                    return EMPTY;
                }),
            )
            .subscribe(() => {
                if (this.data?.data?.status == ReservationStatusType.RECURRING_EVENT) {
                    this.openReservationEditPopup(this.data.data);
                } else {
                    this.navigate.navigateToReservaton(this.data?.data?.prereservationId);
                }
            });
    }

    openCustomer(): void {
        this.navigate.openCustomer(this.data?.data?.customer?.id);
    }

    showDetails(): void {
        this.isSingleClick = true;
        setTimeout(() => {
            if (this.isSingleClick) {
                this.isShowDetails = !this.isShowDetails;
            }
        }, 200);
    }

    private openReservationEditPopup(term: Reservation): void {
        const modalNewTerm: NgbModalRef = this.modalService.open(TermPopupComponent, {
            backdrop: 'static',
            keyboard: false,
            size: 'lg',
            windowClass: 'mf-transparent-popup',
        });
        from(modalNewTerm.result)
            .pipe(
                catchError(() => {
                    return of(null);
                }),
            )
            .subscribe(newEvent => {
                if (newEvent) {
                    term.status = undefined;
                    term.prereservationId = newEvent.prereservationId;
                    if (newEvent.isOpenReservation) {
                        this.navigate.navigateToReservaton(term.prereservationId);
                    }
                }
            });
        this.editRegularReservation(modalNewTerm, term);
    }

    private editRegularReservation(modalNewTerm: NgbModalRef, term: Reservation): void {
        const resRequestData: ReservationRequest = new ReservationRequest();

        resRequestData.reservationMode = ReservationMode.RESERVE;
        resRequestData.event = new Event().deserialize(term);
        resRequestData.isEditMode = true;
        resRequestData.selectedDate = moment(term.reservationFrom);
        resRequestData.selectedTime = moment(term.reservationFrom).format('HH:mm');
        resRequestData.selectedTimeEnd = moment(term.reservationTo).format('HH:mm');
        resRequestData.customerForTerm = new Customer().deserialize(term.customer);
        resRequestData.customTagId = term.customTagId;

        if (term.asset != undefined) {
            resRequestData.selectedAsset = term.asset;
        }
        resRequestData.termComment = term.comment;
        resRequestData.selectedSubcontractor = <Subcontractor>term?.contractor?.subcontractors[0];
        resRequestData.selectedService = term.service;

        if (term.serviceGroup) {
            resRequestData.serviceGroup = term.serviceGroup;
            resRequestData.serviceGroup.type = 'GROUP';
        }
        resRequestData.telemed = term.telemed;
        modalNewTerm.componentInstance.resRequestData = resRequestData;
    }

    private createViewItems$(): Observable<ReservationExaminationViewItem[]> {
        return of(
            this.data?.data?.examinations?.map(examination => {
                return {
                    id: examination.id,
                    name: examination.examination.name,
                    type: examination.examination.type,
                    dropdownDefinition: null,
                    status: examination.status,
                } as ReservationExaminationViewItem;
            }),
        );
    }

    private createProgressView$(): Observable<number> {
        const examinations = this.data?.data?.examinations;
        if (examinations?.length > 0) {
            return of((examinations.filter(res => res.status === ReservationExaminationStatus.OK).length / examinations.length) * 100);
        }
        return of(null);
    }

    private createStatusView$(): Observable<DocumentationStatus[]> {
        const reportStatus: DocumentationStatus = {
            completed: false,
            label: 'Končni izvid',
            statusText: DocumentationKanbanStatus.Default,
        };

        const certificateStatus: DocumentationStatus = {
            completed: false,
            label: 'Spričevalo',
            statusText: DocumentationKanbanStatus.Default,
        };

        const invoiceStatus: DocumentationStatus = {
            completed: false,
            label: 'Račun',
            statusText: InvoiceKanbanStatus.Default,
        };

        const portfolioPapers = this.data?.data?.portfolioPapers;
        if (portfolioPapers?.length > 0) {
            const reports = portfolioPapers.filter(paper => paper.paperType === PaperType.NAPREDNI_IZVID);
            if (reports.length > 0) {
                const lowestStatusPaper = this.findLowestSatatusPaper(reports);
                reportStatus.completed = lowestStatusPaper.shared;
                reportStatus.statusText = this.getPaperStatusText(lowestStatusPaper);
            }

            const certificates = portfolioPapers.filter(paper =>
                [
                    PaperType.WORK_CERTIFICATE,
                    PaperType.DRIVER_CERTIFICATE,
                    PaperType.WEAPON_CERTIFICATE,
                    PaperType.DRIVER_CERTIFICATE_SPECIAL,
                ].includes(paper.paperType),
            );
            if (certificates.length > 0) {
                const lowestStatusPaper = this.findLowestSatatusPaper(certificates);
                certificateStatus.completed = lowestStatusPaper.shared;
                certificateStatus.statusText = this.getPaperStatusText(lowestStatusPaper);
            }
        }
        const invoices = this.data?.data?.invoiceReports;
        const groupInvoices = this.data?.data?.groupInvoiceConfirmations;

        if (groupInvoices?.length > 0) {
            invoiceStatus.statusText = InvoiceKanbanStatus.GroupInvoice;
            invoiceStatus.completed = true;
        } else if (invoices?.length > 0) {
            invoiceStatus.statusText = InvoiceKanbanStatus.Created;
            invoiceStatus.completed = true;
        }

        return of([reportStatus, certificateStatus, invoiceStatus]);
    }

    private findLowestSatatusPaper(papers: PortfolioPaper[]) {
        let lowestStatusPaper = papers[0];
        papers.forEach(paper => {
            if (!paper.shared && !paper.signedPaper) {
                lowestStatusPaper = paper;
            } else if (!paper.shared && paper.signedPaper) {
                if (lowestStatusPaper.shared) {
                    lowestStatusPaper = paper;
                }
            }
        });
        return lowestStatusPaper;
    }

    private getPaperStatusText(paper: Partial<PortfolioPaper>) {
        let statusText = DocumentationKanbanStatus.Prepared;
        if (paper.signedPaper) {
            statusText = DocumentationKanbanStatus.Signed;
        }
        if (paper.shared) {
            statusText = DocumentationKanbanStatus.Shared;
        }
        return statusText;
    }
}
